<template>
  <div class="modal" ref="modalRef" data-bs-backdrop="static" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <form class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ titleRef }}
          </h3>
          <button type="button" class="btn-close" v-bind="{ disabled: busyRef }" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="detailsLoadedRef && hasDetailsRef" class="alert alert-warning alert-sm">
            Existing family details will remain associated with this relationship even if the spouse is changed.
          </div>
          <ProfileCreatorSelector
            ref="creatorSelectorRef"
            :relationship="spouseRelRef"
            :related-item-id="personIdRef"
            v-model:use-existing="useExistingRef"
            v-model:existing-person-id="newSpouseIdRef"
            :validate-existing="validateExisting"/>
        </div>
        <div class="modal-footer">
          <button type="submit" class="btn btn-primary" :disabled="!canChangeRef" @click="changeSpouse">
            Change
          </button>
          <button type="button" class="btn btn-outline-primary" :disabled="busyRef" data-bs-dismiss="modal">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-body {
  padding: 0 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.alert {
  margin: 0;
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { FamilyRelationshipRole, ViewFamily } from '@/rd/ResearchDataModel'
import { NewProfileRelationship, setProfileSpouseAsync } from './ProfileManager'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { Modal } from 'bootstrap'
import ProfileCreatorSelector from './ProfileCreatorSelector.vue'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'

const props = defineProps<{
  asChild?: boolean,
}>()

defineExpose({
  open,
})

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const assertionStore = useAssertionStore()

const profileGroupIdRef = ref<string>()
const viewFamilyRef = ref<ViewFamily>()
const spouseRoleRef = ref(FamilyRelationshipRole.Father)
const useExistingRef = ref(false)
const newSpouseIdRef = ref<string>()
const detailsLoadedRef = ref(false)
const busyRef = ref(false)
const modalRef = ref<HTMLElement>()
const creatorSelectorRef = ref<InstanceType<typeof ProfileCreatorSelector>>()

const titleRef = computed(() => {
  if (props.asChild) {
    if (viewFamilyRef.value?.getSpouseId(spouseRoleRef.value))
      return spouseRoleRef.value == FamilyRelationshipRole.Father ? 'Change father' : 'Change mother'

    return spouseRoleRef.value == FamilyRelationshipRole.Father ? 'Add father' : 'Add mother'
  }
  return 'Change spouse'
})
const personRoleRef = computed(() => spouseRoleRef.value == FamilyRelationshipRole.Father ? FamilyRelationshipRole.Mother : FamilyRelationshipRole.Father)
const personIdRef =  computed(() => viewFamilyRef.value?.getSpouseId(personRoleRef.value))
const spouseRelRef = computed(() => spouseRoleRef.value == FamilyRelationshipRole.Father ? NewProfileRelationship.Husband : NewProfileRelationship.Wife)
const oldSpouseIdRef = computed(() => viewFamilyRef.value?.getSpouseId(spouseRoleRef.value))
const profileFamilyAssertionsRef = computed(() => {
  const profileFamilyId = viewFamilyRef.value?.matchIds.find(id => CompositeId.hasGroupId(id, profileGroupIdRef.value!))
  return assertionStore.getLoadedForFamily(profileFamilyId)
})
const hasDetailsRef = computed(() => detailsLoadedRef.value && profileFamilyAssertionsRef.value.length > 0)
const canChangeRef = computed(() => creatorSelectorRef.value?.isValid && !busyRef.value)

function open(profileGroupId: string, viewFamily: ViewFamily, spouseRole: FamilyRelationshipRole) {
  if (spouseRole == FamilyRelationshipRole.Child)
    throw `Unexpected spouse role ${spouseRole}`

  if (modalRef.value) {
    profileGroupIdRef.value = profileGroupId
    viewFamilyRef.value = viewFamily
    spouseRoleRef.value = spouseRole
    creatorSelectorRef.value?.reset()

    detailsLoadedRef.value = false
    viewFamilyStore.ensureAssertionsLoadedAsync(viewFamily.id!).then(() => {
      detailsLoadedRef.value = true
    })

    Modal.getOrCreateInstance(modalRef.value)?.show()
  }
}

function validateExisting(personId: string) {
  if (personId && personId == oldSpouseIdRef.value) {
    return 'This is the current spouse.'
  }
}

async function changeSpouse(e: Event) {
  e.preventDefault()

  busyRef.value = true
  try {
    const profileFamilyId = viewFamilyRef.value?.matchIds.find(id => CompositeId.hasGroupId(id, profileGroupIdRef.value!))
    if (!profileFamilyId)
      throw `Profile family in group ${profileGroupIdRef.value} not found`

    const viewSpouseId = await creatorSelectorRef.value?.getOrCreateProfileAsync()
    const viewSpouse = await viewPersonStore.getAsyncPerson(viewSpouseId, LoadMode.EnsureLoaded)?.whenLoadCompleted
    if (!viewSpouse)
      throw `Spouse ${viewSpouseId} not found`

    await setProfileSpouseAsync(profileFamilyId, viewSpouse.id!, spouseRoleRef.value)

    Modal.getOrCreateInstance(modalRef.value!)?.hide()
  }
  finally {
    busyRef.value = false
  }
}

</script>